import {
  APIComponentAttributes,
  APIReferenceAttributes,
} from '@ardoq/api-types';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { workspaceAccessControlInterface } from './workspace';
import { componentAccessControlOperation } from './component';
import { FeatureContext } from '@ardoq/features';

type ReferenceCanDoArgs = {
  permissionContext: PermissionContext;
  featureContext: FeatureContext;
  reference: Pick<
    APIReferenceAttributes,
    '_id' | 'rootWorkspace' | 'source' | 'target'
  >;
  component: Pick<
    APIComponentAttributes,
    '_id' | 'rootWorkspace' | 'model' | 'typeId'
  >;
};

const canEditReference = ({
  reference,
  sourceComponent,
  permissionContext,
  featureContext,
}: Pick<
  ReferenceCanDoArgs,
  'reference' | 'permissionContext' | 'featureContext'
> & {
  sourceComponent: ReferenceCanDoArgs['component'] | undefined;
}): boolean => {
  if (!reference) {
    return false;
  }
  const canEditWorkspace = workspaceAccessControlInterface.canEditWorkspace(
    permissionContext,
    reference.rootWorkspace,
    // TODO - RAFAA: For now, we ignore it, so the PR doesn't get too big
    null
  );

  if (canEditWorkspace) {
    return true;
  }

  if (!sourceComponent) {
    return false;
  }

  return componentAccessControlOperation.canEditComponent({
    component: sourceComponent,
    permissionContext,
    featureContext,
  });
};

const canAddReferenceToComponent = ({
  component,
  permissionContext,
  featureContext,
}: Pick<
  ReferenceCanDoArgs,
  'component' | 'permissionContext' | 'featureContext'
>): boolean => {
  return componentAccessControlOperation.canEditComponent({
    component,
    permissionContext,
    featureContext,
  });
};

const canEditComponentReferences =
  componentAccessControlOperation.canEditComponent;

const canEditReferencesTargetComponent = ({
  permissionContext,
  featureContext,
  reference,
  components,
}: {
  reference: APIReferenceAttributes;
  permissionContext: PermissionContext;
  featureContext: FeatureContext;
  components: APIComponentAttributes[];
}): boolean => {
  const componentData = components.find(({ _id }) => reference.target === _id);
  return (
    !!componentData &&
    canEditComponentReferences({
      component: componentData,
      permissionContext,
      featureContext,
    })
  );
};

/**
 * Checks if the user can unlock the reference.
 * @param config  - The configuration object.
 */
const canUnlock = ({
  permissionContext: { user },
  reference,
}: {
  permissionContext: PermissionContext;
  reference: APIReferenceAttributes;
}): boolean => {
  if (!user) {
    return false;
  }
  return user._id === reference.lock || permissionsOperations.isOrgAdmin(user);
};

export const referenceAccessControlOperation = {
  /**
   * Checks if the user can edit reference.
   */
  canEditReference,
  /**
   * Checks if the user can add reference to a component.
   */
  canAddReferenceToComponent,
  /**
   * Checks if the user can unlock the reference.
   */
  canUnlock,
  /**
   * Checks if the user can edit component references.
   */
  canEditComponentReferences,
  /**
   * Checks if the user can edit references target component.
   */
  canEditReferencesTargetComponent,
};
